import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, tenantHeaders, apiErr } from "../lib/api";
import { fmt } from "../lib/format";
import { CartProvider, useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import { toast } from "sonner";
import { ArrowLeft, Loader2, CreditCard, Wallet, Gift, Bike } from "lucide-react";

function nextDays(n) {
  const out = [];
  for (let i = 0; i < n; i++) {
    const d = new Date(); d.setDate(d.getDate() + i);
    out.push(d.toISOString().slice(0, 10));
  }
  return out;
}

function CheckoutInner({ slug, storeSlug }) {
  const cart = useCart();
  const nav = useNavigate();
  const [store, setStore] = useState(null);
  const [ent, setEnt] = useState({});
  const [brand, setBrand] = useState("#FE672E");
  const [orderType, setOrderType] = useState("pickup");
  const [addr, setAddr] = useState("");
  const [cap, setCap] = useState("");
  const [coverage, setCoverage] = useState(null);
  const [delivStatus, setDelivStatus] = useState(null);
  const [form, setForm] = useState({ customer_name: "", customer_phone: "", customer_email: "", note: "", privacy: false });
  const { user: authUser } = useAuth();
  useEffect(() => {
    if (authUser && authUser.role === "customer") {
      setForm((f) => ({ ...f, customer_name: f.customer_name || authUser.name || "", customer_email: f.customer_email || authUser.email || "" }));
    }
  }, [authUser]);
  useEffect(() => {
    if (!authUser || authUser.role !== "customer") { setWelcomeCoupon(null); return; }
    api.get("/account/coupons")
      .then((r) => setWelcomeCoupon((r.data || []).find((c) => c.source === "welcome" && !c.used) || null))
      .catch(() => {});
  }, [authUser]);
  const [pickupMode, setPickupMode] = useState("first");
  const [date, setDate] = useState(nextDays(1)[0]);
  const [slots, setSlots] = useState([]);
  const [slot, setSlot] = useState(null);
  const [payment, setPayment] = useState("online");
  const [busy, setBusy] = useState(false);
  const [coupon, setCoupon] = useState("");
  const [couponInfo, setCouponInfo] = useState(null);
  const [couponErr, setCouponErr] = useState("");
  const [applying, setApplying] = useState(false);
  const [welcomeCoupon, setWelcomeCoupon] = useState(null);
  const [loyalty, setLoyalty] = useState(null);
  const [usePoints, setUsePoints] = useState(false);

  useEffect(() => {
    api.get("/store/config", tenantHeaders(slug, storeSlug)).then((r) => { setStore(r.data.store); setEnt(r.data.entitlements || {}); setBrand(r.data.branding?.primary_color || "#FE672E"); });
  }, [slug, storeSlug]);

  const checkCap = async (v) => {
    setCap(v); setCoverage(null);
    if (v.trim().length >= 5) {
      try { const { data } = await api.get(`/store/delivery/check?cap=${v.trim()}`, tenantHeaders(slug, storeSlug)); setCoverage(data); }
      catch { setCoverage({ covered: false }); }
    }
  };

  useEffect(() => {
    if (pickupMode !== "slot") return;
    api.get(`/store/slots?date=${date}`, tenantHeaders(slug, storeSlug)).then((r) => setSlots(r.data.slots)).catch(() => setSlots([]));
  }, [pickupMode, date, slug, storeSlug]);

  useEffect(() => {
    if (orderType !== "delivery") { setDelivStatus(null); return; }
    api.get("/store/delivery/status", tenantHeaders(slug, storeSlug))
      .then((r) => setDelivStatus(r.data)).catch(() => setDelivStatus(null));
  }, [orderType, slug, storeSlug]);

  useEffect(() => { setCouponInfo(null); setCouponErr(""); }, [orderType]);

  useEffect(() => {
    const pre = localStorage.getItem("pf_prefill_coupon");
    if (!pre || !ent.coupons || couponInfo || cart.items.length === 0) return;
    setCoupon(pre);
    (async () => {
      try {
        const { data } = await api.post("/store/coupon/validate",
          { code: pre, subtotal: cart.total, order_type: orderType, delivery_fee: deliveryFee }, tenantHeaders(slug, storeSlug));
        setCouponInfo(data); toast.success("Coupon di prossimità applicato");
      } catch { /* silently ignore invalid prefill */ }
      localStorage.removeItem("pf_prefill_coupon");
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ent.coupons]);

  useEffect(() => {
    setLoyalty(null); setUsePoints(false);
    const p = form.customer_phone.trim();
    if (p.length < 6) return;
    api.get(`/store/loyalty?phone=${encodeURIComponent(p)}`, tenantHeaders(slug, storeSlug))
      .then((r) => setLoyalty(r.data.enabled ? r.data : null)).catch(() => {});
  }, [form.customer_phone, slug, storeSlug]);

  const storeHome = storeSlug ? `/t/${slug}/${storeSlug}` : `/t/${slug}`;

  if (cart.items.length === 0) {
    return <div className="min-h-screen flex flex-col items-center justify-center gap-3">
      <p className="text-muted-foreground">Carrello vuoto</p>
      <Button onClick={() => nav(storeHome)} variant="outline" className="rounded-full">Torna al menu</Button>
    </div>;
  }

  const deliveryEnabled = store?.delivery_enabled && ent.delivery;
  const zone = coverage?.covered ? coverage.zone : null;
  const flatFee = Number(store?.delivery_fee || 0);
  const flatFreeOver = store?.delivery_free_over ? Number(store.delivery_free_over) : null;
  const deliveryFee = orderType !== "delivery" ? 0
    : (zone ? (zone.free_over && cart.total >= zone.free_over ? 0 : zone.fee)
            : (flatFreeOver && cart.total >= flatFreeOver ? 0 : flatFee));
  const discount = !couponInfo ? 0 : (couponInfo.type === "free_delivery" ? deliveryFee : couponInfo.discount);
  const totalAfterCoupon = Math.max(cart.total + deliveryFee - discount, 0);
  const redeemableEur = loyalty && loyalty.points >= loyalty.min_redeem
    ? Math.min(Math.floor(loyalty.points / loyalty.redeem_cost), Math.floor(totalAfterCoupon)) : 0;
  const loyaltyDiscount = usePoints ? redeemableEur : 0;
  const grandTotal = Math.max(totalAfterCoupon - loyaltyDiscount, 0);

  const applyCoupon = async (codeArg) => {
    const code = (typeof codeArg === "string" ? codeArg : coupon).trim();
    if (!code) return;
    setApplying(true); setCouponErr("");
    try {
      setCoupon(code);
      const { data } = await api.post("/store/coupon/validate",
        { code, subtotal: cart.total, order_type: orderType, delivery_fee: deliveryFee }, tenantHeaders(slug, storeSlug));
      setCouponInfo(data); toast.success("Codice applicato");
    } catch (e) { setCouponInfo(null); setCouponErr(apiErr(e)); }
    finally { setApplying(false); }
  };
  const removeCoupon = () => { setCouponInfo(null); setCoupon(""); setCouponErr(""); };

  const submit = async () => {
    const name = form.customer_name.trim();
    const phone = form.customer_phone.trim();
    const email = form.customer_email.trim();
    if (!name) return toast.error("Inserisci il tuo nome e cognome");
    if (!email) return toast.error("Inserisci la tua email");
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return toast.error("Inserisci un'email valida");
    if (!phone) return toast.error("Inserisci il tuo numero di cellulare");
    if (phone.replace(/\D/g, "").length < 6) return toast.error("Inserisci un numero di cellulare valido");
    if (!form.privacy) return toast.error("Accetta privacy e condizioni");
    if (pickupMode === "slot" && !slot) return toast.error("Seleziona uno slot");
    if (orderType !== "delivery" && cart.total < (store?.min_order || 0)) return toast.error(`Ordine minimo €${(store?.min_order || 0).toFixed(2)}`);
    if (orderType === "delivery") {
      if (!addr) return toast.error("Inserisci l'indirizzo di consegna");
    }
    setBusy(true);
    try {
      const payload = {
        items: cart.items.map((i) => ({ product_id: i.product_id, quantity: i.quantity, options: i.options, note: i.note })),
        customer_name: form.customer_name, customer_phone: form.customer_phone, customer_email: form.customer_email,
        pickup_mode: pickupMode, pickup_at: pickupMode === "slot" ? slot : null,
        note: form.note, payment_method: payment, privacy_accepted: form.privacy,
        order_type: orderType,
        delivery_address: orderType === "delivery" ? addr : null,
        delivery_cap: orderType === "delivery" ? cap.trim() : null,
        coupon_code: couponInfo ? couponInfo.code : null,
        redeem: usePoints,
        store: storeSlug || null,
        client_total: grandTotal,
      };
      const { data } = await api.post("/store/orders", payload, tenantHeaders(slug, storeSlug));
      cart.clear();
      if (data.payment_redirect) nav(data.payment_redirect);
      else { toast.success("Ordine inviato!"); nav(`/order/${data.order_token}`); }
    } catch (e) { toast.error(apiErr(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-[hsl(var(--secondary))]" style={{ "--brand": brand }}>
      <div className="max-w-lg mx-auto px-4 py-6">
        <button onClick={() => nav(storeHome)} className="flex items-center gap-1 text-sm text-muted-foreground mb-4"><ArrowLeft size={16} /> Menu</button>
        <h1 className="font-head text-2xl font-bold mb-5">Checkout</h1>

        {welcomeCoupon && !couponInfo && (
          <div className="bg-[#FFF8E1] border-2 border-[#FFC400] rounded-2xl p-4 mb-4 flex items-center justify-between gap-3" data-testid="welcome-coupon-banner">
            <div className="min-w-0">
              <p className="font-semibold text-sm flex items-center gap-1.5 text-[#0B0B0D]"><Gift size={16} className="text-[#c79400]" /> Coupon di benvenuto</p>
              <p className="text-xs text-neutral-600 mt-0.5">-{welcomeCoupon.discount_value}% sul primo ordine · <span className="font-mono font-semibold">{welcomeCoupon.coupon_code}</span>{welcomeCoupon.min_order ? ` · min €${Number(welcomeCoupon.min_order).toFixed(2)}` : ""}</p>
            </div>
            <Button onClick={() => applyCoupon(welcomeCoupon.coupon_code)} disabled={applying} className="rounded-full bg-[#FFC400] hover:bg-[#E6AC00] text-[#0B0B0D] font-semibold shrink-0" data-testid="welcome-coupon-apply">
              {applying ? <Loader2 className="animate-spin" size={16} /> : "Applica"}
            </Button>
          </div>
        )}

        <div className="bg-card rounded-2xl border p-5 space-y-4">
          <h2 className="font-semibold">I tuoi dati</h2>
          <div><Label>Nome e cognome <span className="text-destructive">*</span></Label><Input data-testid="co-name" required className="mt-1 h-11" value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Cellulare <span className="text-destructive">*</span></Label><Input data-testid="co-phone" required type="tel" className="mt-1 h-11" value={form.customer_phone} onChange={(e) => setForm({ ...form, customer_phone: e.target.value })} /></div>
            <div><Label>Email <span className="text-destructive">*</span></Label><Input data-testid="co-email" required type="email" className="mt-1 h-11" value={form.customer_email} onChange={(e) => setForm({ ...form, customer_email: e.target.value })} /></div>
          </div>
        </div>

        {deliveryEnabled && (
          <div className="bg-card rounded-2xl border p-5 mt-4">
            <h2 className="font-semibold mb-3">Modalità</h2>
            <div className="grid grid-cols-2 gap-2">
              <button onClick={() => setOrderType("pickup")} data-testid="type-pickup" className={`p-3 rounded-xl border text-sm font-semibold ${orderType === "pickup" ? "brand-border brand-soft" : ""}`}>Ritiro in sede</button>
              <button onClick={() => setOrderType("delivery")} data-testid="type-delivery" className={`p-3 rounded-xl border text-sm font-semibold ${orderType === "delivery" ? "brand-border brand-soft" : ""}`}>Consegna a domicilio</button>
            </div>
            {orderType === "delivery" && (
              <div className="mt-4 space-y-3">
                {delivStatus && (
                  <div className="rounded-xl border p-3 bg-[hsl(var(--secondary))] space-y-1.5" data-testid="delivery-status">
                    <div className="flex items-center gap-2 text-sm font-semibold"><Bike size={16} className="brand-text" /> Consegna in ~{delivStatus.eta_min} min</div>
                    <p className="text-xs text-muted-foreground" data-testid="delivery-active-count">
                      {delivStatus.active_deliveries === 0 ? "Nessuna consegna in corso · il rider è disponibile"
                        : delivStatus.active_deliveries === 1 ? "1 consegna in corso in questo momento"
                        : `${delivStatus.active_deliveries} consegne in corso in questo momento`}
                    </p>
                    {delivStatus.busy && (
                      <p className="text-xs font-medium text-amber-600 flex items-center gap-1" data-testid="delivery-busy">
                        ⚠️ Il locale è molto richiesto, i tempi potrebbero allungarsi
                      </p>
                    )}
                  </div>
                )}
                <div><Label>Indirizzo <span className="text-destructive">*</span></Label><Input value={addr} onChange={(e) => setAddr(e.target.value)} data-testid="del-address" className="mt-1 h-11" placeholder="Via e numero civico" /></div>
                <div><Label>CAP <span className="text-muted-foreground text-xs">(facoltativo)</span></Label><Input value={cap} onChange={(e) => checkCap(e.target.value)} data-testid="del-cap" className="mt-1 h-11" placeholder="10121" /></div>
                {coverage?.covered && (
                  <p className="text-sm text-[hsl(var(--success))]" data-testid="cap-ok">✓ {coverage.zone.name} · consegna {fmt.eur(coverage.zone.fee)}{coverage.zone.free_over ? ` · gratis oltre ${fmt.eur(coverage.zone.free_over)}` : ""}</p>
                )}
              </div>
            )}
          </div>
        )}

        <div className="bg-card rounded-2xl border p-5 space-y-4 mt-4">
          <h2 className="font-semibold">{orderType === "delivery" ? "Orario consegna" : "Ritiro in sede"}</h2>
          <RadioGroup value={pickupMode} onValueChange={setPickupMode} className="space-y-2">
            <label className="flex items-center gap-2 cursor-pointer"><RadioGroupItem value="first" data-testid="pickup-first" /> Prima disponibilità (~{store?.prep_time} min)</label>
            <label className="flex items-center gap-2 cursor-pointer"><RadioGroupItem value="slot" data-testid="pickup-slot" /> Scegli un orario</label>
          </RadioGroup>
          {pickupMode === "slot" && (
            <div>
              <div className="flex gap-2 overflow-x-auto no-scrollbar pb-2">
                {nextDays(store?.max_days_ahead || 3).map((d) => (
                  <button key={d} onClick={() => setDate(d)} className={`px-3 py-1.5 rounded-full text-sm whitespace-nowrap ${date === d ? "brand-bg" : "border bg-card"}`}>
                    {new Date(d).toLocaleDateString("it-IT", { weekday: "short", day: "2-digit", month: "2-digit" })}
                  </button>
                ))}
              </div>
              <div className="grid grid-cols-4 gap-2 mt-2 max-h-40 overflow-y-auto">
                {slots.length === 0 && <p className="col-span-4 text-sm text-muted-foreground">Nessuno slot disponibile</p>}
                {slots.map((s) => (
                  <button key={s.time} disabled={s.full} data-testid={`slot-${s.label}`} onClick={() => setSlot(s.time)}
                    className={`py-2 rounded-lg text-sm ${s.full ? "opacity-40 line-through" : slot === s.time ? "brand-bg" : "border bg-card"}`}>{s.label}</button>
                ))}
              </div>
            </div>
          )}
          <Textarea placeholder="Nota generale per l'ordine" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} data-testid="co-note" />
        </div>

        <div className="bg-card rounded-2xl border p-5 space-y-3 mt-4">
          <h2 className="font-semibold">Pagamento</h2>
          <button onClick={() => setPayment("online")} data-testid="pay-online" className={`w-full flex items-center gap-3 p-3 rounded-xl border ${payment === "online" ? "brand-border brand-soft" : ""}`}>
            <CreditCard size={20} className="brand-text" /><div className="text-left"><p className="font-semibold text-sm">Paga online (myPOS)</p><p className="text-xs text-muted-foreground">Pagamento sicuro sulla pagina myPOS</p></div>
          </button>
          {store?.pay_at_pickup && (
            <button onClick={() => setPayment("pickup")} data-testid="pay-pickup" className={`w-full flex items-center gap-3 p-3 rounded-xl border ${payment === "pickup" ? "brand-border brand-soft" : ""}`}>
              <Wallet size={20} /><div className="text-left"><p className="font-semibold text-sm">Paga al ritiro</p><p className="text-xs text-muted-foreground">In cassa quando ritiri</p></div>
            </button>
          )}
        </div>

        {ent.coupons && (
          <div className="bg-card rounded-2xl border p-5 mt-4">
            <h2 className="font-semibold mb-3">Codice sconto</h2>
            {couponInfo ? (
              <div className="flex items-center justify-between bg-[hsl(var(--success))]/10 border border-[hsl(var(--success))]/30 rounded-xl p-3">
                <span className="text-sm font-semibold text-[hsl(var(--success))]" data-testid="coupon-applied">✓ {couponInfo.code} applicato</span>
                <Button variant="ghost" size="sm" onClick={removeCoupon} data-testid="coupon-remove">Rimuovi</Button>
              </div>
            ) : (
              <div className="flex gap-2">
                <Input value={coupon} onChange={(e) => setCoupon(e.target.value.toUpperCase())} placeholder="ES. BENVENUTO10" data-testid="coupon-input" className="h-11" />
                <Button onClick={() => applyCoupon()} disabled={applying} variant="outline" className="rounded-full px-6" data-testid="coupon-apply">{applying ? <Loader2 className="animate-spin" size={16} /> : "Applica"}</Button>
              </div>
            )}
            {couponErr && <p className="text-sm text-destructive mt-2" data-testid="coupon-error">{couponErr}</p>}
          </div>
        )}

        {loyalty && (
          <div className="bg-card rounded-2xl border p-5 mt-4">
            <h2 className="font-semibold mb-2">Punti fedeltà</h2>
            <p className="text-sm text-muted-foreground">Hai <b data-testid="loyalty-balance">{loyalty.points}</b> punti su questo numero.</p>
            {redeemableEur > 0 ? (
              <label className="flex items-center gap-2 mt-3 text-sm cursor-pointer">
                <Checkbox checked={usePoints} onCheckedChange={(v) => setUsePoints(!!v)} data-testid="loyalty-use" className="mt-0.5" />
                <span>Usa i punti per uno sconto di <b>{fmt.eur(redeemableEur)}</b> ({redeemableEur * loyalty.redeem_cost} punti)</span>
              </label>
            ) : (
              <p className="text-xs text-muted-foreground mt-2">Servono almeno {loyalty.min_redeem} punti per ottenere uno sconto.</p>
            )}
          </div>
        )}

        <div className="bg-card rounded-2xl border p-5 mt-4">
          <div className="flex justify-between text-sm"><span>Subtotale</span><span>{fmt.eur(cart.total)}</span></div>
          {orderType === "delivery" && <div className="flex justify-between text-sm mt-1"><span>Consegna</span><span>{deliveryFee === 0 ? "Gratis" : fmt.eur(deliveryFee)}</span></div>}
          {discount > 0 && <div className="flex justify-between text-sm mt-1 text-[hsl(var(--success))]" data-testid="co-discount"><span>Sconto {couponInfo?.code}</span><span>-{fmt.eur(discount)}</span></div>}
          {loyaltyDiscount > 0 && <div className="flex justify-between text-sm mt-1 text-[hsl(var(--success))]" data-testid="co-loyalty"><span>Punti fedeltà</span><span>-{fmt.eur(loyaltyDiscount)}</span></div>}
          <div className="flex justify-between font-head text-lg font-bold mt-2"><span>Totale</span><span>{fmt.eur(grandTotal)}</span></div>
          <label className="flex items-start gap-2 mt-4 text-sm cursor-pointer">
            <Checkbox checked={form.privacy} onCheckedChange={(v) => setForm({ ...form, privacy: !!v })} data-testid="co-privacy" className="mt-0.5" />
            <span>Accetto la <span className="underline">privacy policy</span> e le <span className="underline">condizioni di vendita</span>.</span>
          </label>
        </div>

        {orderType !== "delivery" && store?.min_order > 0 && cart.total < store.min_order && (
          <p className="text-sm text-destructive mt-4 text-center" data-testid="co-min-order">Ordine minimo €{store.min_order.toFixed(2)} — aggiungi ancora {fmt.eur(store.min_order - cart.total)}</p>
        )}
        <Button onClick={submit} disabled={busy || (orderType !== "delivery" && store?.min_order > 0 && cart.total < store.min_order)} data-testid="place-order"
                className="w-full h-14 brand-bg hover:opacity-90 rounded-full font-semibold text-base mt-4 disabled:opacity-50">
          {busy ? <Loader2 className="animate-spin" /> : payment === "online" ? `Invia ordine per conferma · ${fmt.eur(grandTotal)}` : `Conferma ordine · ${fmt.eur(grandTotal)}`}
        </Button>
      </div>
    </div>
  );
}

export default function Checkout() {
  const { slug, storeSlug } = useParams();
  return <CartProvider slug={storeSlug ? `${slug}/${storeSlug}` : slug}><CheckoutInner slug={slug} storeSlug={storeSlug} /></CartProvider>;
}
